import React from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { paths } from './paths';
import { ErrorPage } from '@/pages/ErrorPage';
import { CreateNewBusinessPage } from '@/pages/CreateNewBusinessPage';
import { NewBusinessPage } from '@/pages/NewBusinessPage';
import ViewDealPage from '@/pages/ViewDealPage';
import { HomePage } from '@/pages/HomePage';
import { ClaimsPage } from '@/pages/ClaimsPage';
import { CreateClaimPage } from '@/pages/CreateClaimPage';
import { RenewalsPage } from '@/pages/RenewalsPage';
import { TasksPage } from '@/pages/TasksPage';
import ViewClientPage from '@/pages/ViewClientPage';
import { EndorsementsPage } from '@/pages/Endorsements';
import { CreateEndorsementPage } from '@/pages/CreateEndorsementPage';
import { CreateRenewalPage } from '@/pages/CreateRenewalPage';
import DealSOAFormPage from '@/pages/DealSOAFormPage';
import { PageNotFoundPage } from '@/pages/PageNotFoundPage';
import { SettingsPage } from '@/pages/SettingsPage';
import AdminConsentPage from '@/pages/AdminConsentPage';
import AdminConsentProcessPage from '@/pages/AdminConsentProcessPage';
import { AppLayout } from '@/components/layouts/AppLayout';
import { ProspectsPage } from '@/pages/ProspectsPage';
import { RelationshipsPage } from '@/pages/RelationshipsPage';
import { TemplatesPage } from '@/pages/TemplatesPage';
import { UnlayerTemplatesPage } from '@/pages/UnlayerTemplatesPage';
import { UnlayerDocumentTemplatesPage } from '@/pages/UnlayerDocumentTemplatesPage';
import { AutomationsPage } from '@/pages/AutomationsPage';
import {
	determineTabChildren,
	getClientTabs,
	getDealTabs,
	getDebtorTabs,
	getRelationshipCompanyTabs,
	getSettingsTabs,
	getTemplateTabs,
	getUnlayerEmailTabs,
} from './tabs';
import { CreateDebtorsPage } from '@/pages/CreateDebtorPage';
import ViewDebtorsPage from '@/pages/ViewDebtorsPage';
import ViewRelationshipCompanyPage from '@/pages/ViewRelationshipCompanyPage';
import { DebtorsPage } from '@/pages/DebtorsPage';
import { CreateClientPage } from '@/pages/CreateClientPage';
import TasksTable from '@/components/tables/TasksTable';
import { AuditPage } from '@/pages/AuditPage';
import { AdminLayout } from '@/admin/AdminLayout';
import { adminRoutes } from '@/admin/adminRoutes';
export { paths };

export const getRouter = () => {
	return createBrowserRouter([
		{
			path: '/',
			element: <AppLayout />,
			errorElement: <ErrorPage />,
			children: [
				{
					index: true,
					element: <HomePage />,
				},
				{
					path: paths.prospects,
					element: <ProspectsPage />,
				},
				{
					path: paths.relationships,
					element: <RelationshipsPage />,
				},
				{
					path: `${paths.companies}/:relationshipCompanyId`,
					element: <ViewRelationshipCompanyPage />,
					children: determineTabChildren(getRelationshipCompanyTabs()),
				},
				{
					path: paths.createNewBusiness,
					element: <CreateNewBusinessPage />,
				},
				{
					path: paths.createClient,
					element: <CreateClientPage />,
				},
				{
					path: paths.newBusiness,
					element: <NewBusinessPage />,
				},
				{
					path: `${paths.newBusiness}/:dealId`,
					element: <ViewDealPage />,
					children: determineTabChildren(getDealTabs(paths.newBusiness)),
				},
				...[paths.newBusiness, paths.renewals].map((prefix) => ({
					path: `${prefix}/:dealId${paths.generateSOA}`,
					element: <DealSOAFormPage />,
				})),
				{
					path: paths.claims,
					element: <ClaimsPage />,
				},
				// {
				// 	path: paths.tasks,
				// 	element: <TasksPage />,
				// },
				{
					path: paths.tasks,
					element: <TasksPage />,
					children: [
						{
							index: true,
							element: <Navigate to='in-progress' replace />,
						},
						{
							path: '',
							element: <Navigate to='in-progress' replace />,
						},
						{
							path: 'in-progress',
							element: <TasksTable tab='in-progress' />,
						},
						{
							path: 'completed',
							element: <TasksTable tab='completed' />,
						},
					],
				},
				{
					path: `${paths.claims}/:dealId`,
					element: <ViewDealPage />,
					children: determineTabChildren(getDealTabs(paths.claims)),
				},
				{
					path: paths.createClaim,
					element: <CreateClaimPage />,
				},
				{
					path: paths.renewals,
					element: <RenewalsPage />,
				},
				{
					path: `${paths.debtors}/:dealId`,
					element: <ViewDebtorsPage />,
					children: determineTabChildren(getDebtorTabs(paths.debtors)),
				},
				{
					path: paths.debtors,
					element: <DebtorsPage />,
				},
				{
					path: `${paths.renewals}/:dealId`,
					element: <ViewDealPage />,
					children: determineTabChildren(getDealTabs(paths.renewals)),
				},
				// {
				// 	path: `${paths.renewals}/:dealId${paths.generateSOA}`,
				// 	element: <DealSOAFormPage />,
				// 	errorElement: <ErrorPage />
				// },
				{
					path: paths.createRenewal,
					element: <CreateRenewalPage />,
				},
				{
					path: paths.clients,
					element: <Navigate to='/' replace />,
				},
				...[paths.clients, paths.prospects].map((prefix) => ({
					path: `${prefix}/:clientId`,
					element: <ViewClientPage />,
					children: determineTabChildren(getClientTabs(prefix)),
				})),
				{
					path: paths.endorsements,
					element: <EndorsementsPage />,
				},
				{
					path: `${paths.endorsements}/:dealId`,
					element: <ViewDealPage />,
					children: determineTabChildren(getDealTabs(paths.endorsements)),
				},
				{
					path: `${paths.deals}/:dealId`,
					element: <ViewDealPage />,
					children: determineTabChildren(getDealTabs(paths.deals)),
				},
				{
					path: paths.createEndorsement,
					element: <CreateEndorsementPage />,
				},

				{
					path: paths.settings,
					element: <SettingsPage />,
					children: determineTabChildren(getSettingsTabs(paths.settings)),
				},
				{
					path: paths.templates,
					element: <TemplatesPage />,
					children: determineTabChildren(getTemplateTabs(paths.templates)),
				},
				{
					path: paths.unlayerTemplates,
					element: <UnlayerTemplatesPage />,
					children: determineTabChildren(getUnlayerEmailTabs(paths.unlayerTemplates)),
				},
				{
					path: paths.unlayerDocumentTemplates,
					element: <UnlayerDocumentTemplatesPage />,
				},
				{
					path: paths.automations,
					element: <AutomationsPage />,
				},
				{
					path: paths.audit,
					element: <AuditPage />,
				},
				{
					path: paths.createDebtor,
					element: <CreateDebtorsPage />,
				},
				{
					path: paths.admin,
					element: <AdminLayout />,
					children: adminRoutes,
				},
				{ path: paths.adminConsent, element: <AdminConsentPage /> },
			],
		},
		// Microsoft redirects here after admin consent, so it renders without the signed-in layout
		{
			path: paths.adminConsentProcess,
			element: <AdminConsentProcessPage />,
			errorElement: <ErrorPage />,
		},
		{
			path: '*',
			element: <PageNotFoundPage />,
			errorElement: <ErrorPage />,
		},
	]);
};

export default getRouter;
